import { CACHE_MS, inCohort, parseVariant } from "./activation";
import { safely } from "./contracts";
import { API_NAMES } from "./registry";
import type {
  createEarlyRequestBuffer,
  EarlyRequestEntry,
} from "./earlyRequestBuffer";

/** Match the activation cache's version/scope/age/cohort rules, without storage writes. */
export function mayBufferEarlyRequest(input: {
  localMode: boolean;
  cachedDecision?: string | null;
  scope: string;
  bucket?: number;
  now: number;
}): boolean {
  return safely(() => {
    if (input.localMode === true) return true;
    const raw = input.cachedDecision;
    if (typeof raw !== "string" || !raw || raw.length > 1024) return false;
    const cached = JSON.parse(raw);
    if (
      !cached ||
      cached.version !== 1 ||
      cached.scope !== input.scope ||
      typeof cached.fetchedAt !== "number" ||
      !Number.isFinite(cached.fetchedAt) ||
      !Number.isFinite(input.now) ||
      input.now < cached.fetchedAt ||
      input.now - cached.fetchedAt > CACHE_MS
    )
      return false;
    const name = parseVariant({
      success: true,
      data: { is_enabled: true, variant_name: cached.name },
    });
    if (!name || name === "OFF") return false;
    return inCohort(
      input.bucket,
      Number(name.slice(name.lastIndexOf("_") + 1))
    );
  }, false);
}

type Buffer = ReturnType<typeof createEarlyRequestBuffer>;
type PhysicalRequest = { method: string; url?: string; origin: string };
export interface ConfiguredRequestSource<Token, Observation> {
  captureRequest(name: string): Token | undefined;
  startRequest(token: Token, request: PhysicalRequest): Observation | undefined;
  recordEarlyRequest(entry: Readonly<EarlyRequestEntry>): boolean | void;
}

/** Call-time request ownership; supply the SDK adapter only after configuration. */
export function createEarlyRequestCapture<Token, Observation>(deps: {
  mayBuffer(): boolean;
  createBuffer(): Buffer;
}) {
  let live: ConfiguredRequestSource<Token, Observation> | undefined;
  let buffer: Buffer | undefined;
  let killed = false;
  let revision = 0;
  return {
    captureRequest(name: string) {
      return safely(() => {
        if (!API_NAMES.some((registered) => registered === name)) return;
        const owner = live;
        if (owner) {
          const token = owner.captureRequest(name);
          // A rejected live capture must never fall back to retroactive capture.
          if (token === undefined) return;
          return {
            kind: "live" as const,
            startRequest: (request: PhysicalRequest) =>
              safely(() => owner.startRequest(token, request), undefined),
          };
        }
        // No buffer construction, capture clocks, timers or capture calls for excluded users.
        if (killed || deps.mayBuffer() !== true) return;
        const early = buffer ?? (buffer = deps.createBuffer());
        const token = early.captureRequest(name);
        if (!token) return;
        return {
          kind: "buffered" as const,
          startRequest: (request: PhysicalRequest) =>
            typeof request.url === "string"
              ? early.startRequest(token, { ...request, url: request.url })
              : undefined,
        };
      }, undefined);
    },
    /** Invoke inline immediately after a successful/current configure() await. No extra microtask. */
    configured(
      source: ConfiguredRequestSource<Token, Observation>,
      reportHome?: () => void
    ) {
      const expected = ++revision;
      live = source;
      safely(
        () => buffer?.flush((entry) => source.recordEarlyRequest(entry)),
        undefined
      );
      // Every completed supplier has been handed to the SDK before Home can finish.
      if (expected === revision && live === source)
        safely(() => reportHome?.(), undefined);
    },
    discard() {
      revision++;
      killed = true;
      live = undefined;
      safely(() => buffer?.discard(), undefined);
      buffer = undefined;
    },
  };
}
